"use client";

import React, { useId } from "react";
import { Box } from "@mui/material";
import { localized } from "src/components/art/artwork-types";
import type { CanvasVariant, VariantAxis } from "./variant-resolver";
import {
  FINISH_AXES,
  finishChoices,
  type Finish,
  type FinishAxis,
} from "./finish-options";
import OptionCard, { StepHeading } from "./OptionCard";
import { ChoiceChip, CompactHeading, chipRowSx } from "./CompactOptions";

export interface FinishPickerProps {
  variants: CanvasVariant[];
  axes: VariantAxis[];
  finish: Finish;
  locale: string;
  onChange: (key: FinishAxis, value: string) => void;
  t: (key: string) => string;
  /**
   * Phones under a live preview (the map page): a small label and a row of
   * chips per question instead of numbered steps of large cards.
   */
  compact?: boolean;
  /**
   * Ask only these questions; the rest are asked elsewhere on the page (the
   * art quick-add sheet asks the material itself, with prices). All when
   * omitted.
   */
  only?: FinishAxis[];
}

/** Per-axis heading in the shop's words; the definition's label is the fallback. */
const HEADING_KEY: Record<FinishAxis, string> = {
  material: "canvasMaterial",
  mount: "canvasMount",
  surface: "canvasSurface",
};

/**
 * Material, then frame, each as a numbered step of large cards.
 *
 * Each card says what the choice means ("ready to hang", "arrives rolled in a
 * tube"), because "Platno / Papir" alone asks the customer to already know the
 * difference. An axis with a single sellable answer is not asked — which is
 * how the frame step disappears for paper.
 *
 * No price on these cards. They used to carry "od 9,90 €" each — the cheapest
 * size reachable through that answer — so the same small number sat under
 * material, frame and surface alike and said nothing about THIS canvas; the
 * cheapest paper print is not what someone choosing "Platno" will pay. The
 * one list where a price is exact for the finish already chosen is the size
 * list, and that is the only place it is shown.
 */
export default function FinishPicker({
  variants,
  axes,
  finish,
  locale,
  onChange,
  t,
  compact = false,
  only,
}: FinishPickerProps) {
  const baseId = useId();

  // A missing key comes back as the key itself; fall back to the definition's
  // own label, which the admin keeps in Slovenian and English.
  const translate = (key: string, fallback: string) => {
    const text = t(key);
    return text && text !== key ? text : fallback;
  };

  const rows = FINISH_AXES.map((key) => {
    const axis = axes.find((a) => a.key === key);
    return { key, axis, choices: finishChoices(variants, axes, finish, key) };
  }).filter(
    (row) =>
      row.choices.length > 1 && finish[row.key] != null && (!only || only.includes(row.key)),
  );

  if (!rows.length) return null;

  return (
    <>
      {rows.map(({ key, axis, choices }) => {
        const headingId = `${baseId}-${key}`;
        const heading = translate(HEADING_KEY[key], axis ? localized(axis.label, locale) : key);
        const label = (value: string) => {
          const def = axis?.values.find((x) => x.value === value);
          return translate(`${HEADING_KEY[key]}_${value}`, def ? localized(def.label, locale) : value);
        };
        if (compact) {
          return (
            <Box key={key}>
              <CompactHeading id={headingId}>{heading}</CompactHeading>
              <Box role="radiogroup" aria-labelledby={headingId} sx={chipRowSx}>
                {choices.map((value) => (
                  <ChoiceChip
                    key={value}
                    selected={finish[key] === value}
                    onClick={() => onChange(key, value)}
                  >
                    {label(value)}
                  </ChoiceChip>
                ))}
              </Box>
            </Box>
          );
        }
        return (
          <Box key={key}>
            <StepHeading id={headingId}>{heading}</StepHeading>
            <Box
              role="radiogroup"
              aria-labelledby={headingId}
              sx={{
                display: "grid",
                // Two across even on a phone: every finish question has two
                // answers, and side by side they read as a choice between
                // them rather than as two separate rows to scroll past.
                gridTemplateColumns: "1fr 1fr",
                gap: 1.5,
              }}
            >
              {choices.map((value) => {
                const valueKey = `${HEADING_KEY[key]}_${value}`;
                const hint = t(`${valueKey}_hint`);
                return (
                  <OptionCard
                    key={value}
                    selected={finish[key] === value}
                    onClick={() => onChange(key, value)}
                    title={label(value)}
                    subtitle={hint && hint !== `${valueKey}_hint` ? hint : undefined}
                  />
                );
              })}
            </Box>
          </Box>
        );
      })}
    </>
  );
}
